import { CurrencyCode } from "@redotech/shopify-client/storefront.graphql";
import { getPlainDateFromDateUtc } from "@redotech/util/date";
import { sharedEmailSchemaFields } from "../../../email-builder/shared-schema-fields";
import { AdvancedFlowTokenType } from "../../type-system/data-types/base-types";
import { defineSchema, SchemaInstance } from "../../type-system/schema";

try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  require("temporal-polyfill/global"); // useful for storybook (since it uses webpack not bazel)
} catch (e) {
  // do nothing, this is only for storybook
}

const discountCodeSchema = defineSchema({
  fields: {
    discountCode: {
      dataType: "Maybe Text",
      documentation: "The discount code to share with the customer.",
      hideFromConditionStep: true,
    },
  },
});

export enum MarketingEntryPoint {
  SIGNUP_FORM = "form",
  SHOPIFY_SYNC = "shopify",
  BACKFILL = "backfill",
  COMMENTSOLD_SYNC = "commentsold",
  SAVE_FOR_LATER = "save-for-later",
  LOGIN = "login",
  ORDER_TRACKING = "order-tracking",
  IMPORT = "import",
  UNKNOWN = "unknown",
  MERCHANT = "merchant",
  WARRANTY_REGISTRATION = "warranty-registration",
  META_LEAD_ADS = "meta-lead-ads",
  POSTSCRIPT = "postscript",
  ORDER_TRACKING_PAGE = "order-tracking-page",
  TEXT_TO_SUBSCRIBE = "text-to-subscribe",
  TEXT_KEYWORD_TO_SUBSCRIBE = "text-keyword-to-subscribe",
  PUBLIC_API = "public-api",
  ALIA = "alia",
}

export const marketingEntryPointPrettyNames: Record<
  MarketingEntryPoint,
  string
> = {
  [MarketingEntryPoint.SIGNUP_FORM]: "Signup form",
  [MarketingEntryPoint.SHOPIFY_SYNC]: "Shopify sync",
  [MarketingEntryPoint.BACKFILL]: "Backfill",
  [MarketingEntryPoint.COMMENTSOLD_SYNC]: "Commentsold sync",
  [MarketingEntryPoint.SAVE_FOR_LATER]: '"Save for later" panel',
  [MarketingEntryPoint.LOGIN]: "Customer account login",
  [MarketingEntryPoint.ORDER_TRACKING]: "Order tracking",
  [MarketingEntryPoint.IMPORT]: "CSV Import",
  [MarketingEntryPoint.UNKNOWN]: "Unknown",
  [MarketingEntryPoint.MERCHANT]: "Manual subscribe",
  [MarketingEntryPoint.WARRANTY_REGISTRATION]: "Warranty registration",
  [MarketingEntryPoint.META_LEAD_ADS]: "Meta lead ads",
  [MarketingEntryPoint.POSTSCRIPT]: "Postscript",
  [MarketingEntryPoint.ORDER_TRACKING_PAGE]: "Order tracking page",
  [MarketingEntryPoint.TEXT_TO_SUBSCRIBE]: "Text to subscribe",
  [MarketingEntryPoint.TEXT_KEYWORD_TO_SUBSCRIBE]: "Text keyword to subscribe",
  [MarketingEntryPoint.PUBLIC_API]: "Public API",
  [MarketingEntryPoint.ALIA]: "Alia",
};

export const baseMarketingSchemaWithoutInteractiveCart = defineSchema({
  fields: {
    redoCustomerId: {
      dataType: "Text",
      documentation: "The Redo customer ID.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    customerFirstName: {
      dataType: "Maybe Text",
      documentation:
        "The first name of the customer who signed up for marketing messages.",
      hideFromConditionStep: true,
    },
    customerLastName: {
      dataType: "Maybe Text",
      documentation:
        "The last name of the customer who signed up for marketing messages.",
      hideFromConditionStep: true,
    },
    customerFullName: {
      dataType: "Maybe Text",
      documentation:
        "The full name of the customer who signed up for marketing messages.",
      hideFromConditionStep: true,
    },
  },
});

export const baseMarketingSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    cartContext: {
      dataType: "Cart Context",
      documentation:
        "Data that is useful for generating product recommendations for an interactive cart",
    },
  },
});

export const baseMarketingEmailSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    customerEmail: {
      dataType: "Email",
      documentation: "The email address of the customer.",
    },
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from email updates. Only displays when the customer is subscribed",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation:
        "The link to unsubscribe from email updates with one click for email delivery compliance. Only displays when the customer is subscribed",
    },
    ...sharedEmailSchemaFields,
  },
});

export const baseMarketingBackInStockSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    productVariantId: {
      dataType: "Text",
      documentation: "The product variant ID that is out of stock.",
    },
    backInStockProductUrl: {
      dataType: "Maybe Url",
      documentation: "The URL to the product page.",
    },
    backInStockProductTitle: {
      dataType: "Maybe Text",
      documentation: "The title of the product that is back in stock.",
    },
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from email updates. Only displays when the customer is subscribed",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    oldInventoryCount: {
      dataType: "Maybe Number",
      documentation: "The inventory count for the product variant pre-restock.",
    },
    newInventoryCount: {
      dataType: "Maybe Number",
      documentation:
        "The inventory count for the product variant post-restock.",
    },
  },
});

export const baseMarketingLowInventorySchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    productVariantId: {
      dataType: "Text",
      documentation: "The product variant ID that is out of stock.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
  },
});

export const baseMarketingSignupSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    alreadySubscribed: {
      dataType: "Boolean",
      documentation:
        "Whether the customer was already subscribed to marketing messages.",
    },
    entryPoint: {
      dataType: "Enum",
      values: Object.values(MarketingEntryPoint).filter(
        (value) =>
          // These entry points don't trigger flows
          ![MarketingEntryPoint.MERCHANT, MarketingEntryPoint.IMPORT].includes(
            value,
          ),
      ) as MarketingEntryPoint[],
      documentation: "Where the customer signed up for marketing messages.",
      prettyValues: marketingEntryPointPrettyNames,
    },
    formId: {
      dataType: "Maybe Text",
      documentation: "The form that the customer signed up through, if any.",
    },
    customerEventId: {
      dataType: "Maybe Text",
      documentation: "The ID of the customer event that triggered the signup.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
  },
});

export const customerGroupMembershipChangeSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned the cart.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer who abandoned the cart.",
    },
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from email updates. Only displays when the customer is subscribed",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation:
        "The link to unsubscribe from email updates with one click for email delivery compliance. Only displays when the customer is subscribed",
    },
    segment: {
      dataType: "Token",
      tokenType: AdvancedFlowTokenType.CUSTOMER_GROUP_ID,
      documentation: "The segment that the customer entered or exited.",
    },
  },
});

export const emailMarketingSignupSchema = defineSchema({
  fields: {
    ...baseMarketingSignupSchema.fields,
    ...discountCodeSchema.fields,
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from email updates. Only displays when the customer is subscribed",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation:
        "The link to unsubscribe from email updates with one click for email delivery compliance. Only displays when the customer is subscribed",
    },
    customerEmail: {
      dataType: "Email",
      documentation:
        "The email address of the customer who signed up for marketing emails.",
    },
  },
});

const baseAbandonmentSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from email updates. Only displays when the customer is subscribed",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation:
        "The link to unsubscribe from email updates with one click for email delivery compliance. Only displays when the customer is subscribed",
    },
    customerEventId: {
      dataType: "Maybe Text",
      documentation:
        "The ID of the customer event that triggered the abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
  },
});

export const baseMarketingCartAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "The time the last item was added to the cart.",
    },
    isCartAbandoned: {
      dataType: "Boolean",
      documentation:
        "If the customer has not begun the checkout process or checked out since updating their cart.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned the cart.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer who abandoned the cart.",
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the store that the customer abandoned.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
  },
});

export const baseMarketingBrowseAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the browsing last occurred.",
    },
    isBrowseAbandoned: {
      dataType: "Boolean",
      documentation:
        "If the customer has not added anything to the cart or placed an order since browsing.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
  },
});

const dummyCartContext = (generatedByRedo: boolean) => ({
  existingCartId: generatedByRedo ? "preview" : null,
  productIdsForRecommendations: [],
  productIdsAlreadyInCart: [],
  alreadyInCart: [],
});

const exampleBaseMarketingSchemaInstance: SchemaInstance<
  typeof baseMarketingSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: undefined,
  customerLastName: undefined,
  customerFullName: undefined,
  cartContext: dummyCartContext(true),
};

export const baseMarketingCheckoutAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the last customer action on checkout occurred.",
    },
    isCheckoutAbandoned: {
      dataType: "Boolean",
      documentation:
        "If the customer has not placed an order since starting checkout.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
    checkout: {
      dataType: "Maybe Shopify Checkout",
      documentation: "Data about the customer's checkout session",
    },
    // TODO: booka66 - Remove this legacy cartSubtotal field once all legacy cart subtotal automations are migrated to use cartCondition
    cartSubtotal: {
      dataType: "Maybe Number",
      documentation:
        "The subtotal amount of the abandoned cart (in the store's currency).",
    },
    cartProductCount: {
      dataType: "Maybe Number",
      documentation: "The number of unique products in the abandoned cart.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    cartCondition: {
      dataType: "Checkout Context Predicate",
      documentation:
        "Branch based on products in the abandoned cart (collections, specific products, tags, product count, or subtotal).",
      hideFromInsertionModal: true,
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the store that the customer abandoned.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
    productInCartName: {
      dataType: "Maybe Text",
      documentation: "The name of the first product in the abandoned cart.",
    },
    sourceName: {
      dataType: "Maybe Text",
      documentation:
        "The source of the checkout: web, pos, shopify_draft_order, etc.",
      hideFromInsertionModal: true,
    },
  },
});

const exampleAbandonmentSchemaInstance: SchemaInstance<
  typeof baseAbandonmentSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  isAbandonment: true,
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  customerEventId: "67f8d6d38fd4c4b4d1c19876",
};

export const exampleBaseMarketingCheckoutAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCheckoutAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  isCheckoutAbandoned: true,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  checkout: {
    attributes: [],
    billingAddress: null,
    currencyCode: CurrencyCode.Usd,
    discountApplications: [],
    email: "john.doe@example.com",
    lineItems: [],
    order: null,
    shippingAddress: null,
    shippingLine: null,
    subtotalPrice: { amount: 100, currencyCode: CurrencyCode.Usd },
    phone: "+1234567890",
    token: "",
    totalPrice: { amount: 105, currencyCode: CurrencyCode.Usd },
    totalTax: { amount: 5, currencyCode: CurrencyCode.Usd },
    transactions: [],
  },
  cartSubtotal: 100,
  cartProductCount: 3,
  cartCondition: false,
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
  productInCartName: "Example Product",
  sourceName: "web",
};

export const exampleBaseMarketingBrowseAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingBrowseAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isBrowseAbandoned: true,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
};

export const exampleBaseMarketingCartAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCartAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isCartAbandoned: true,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
};

export const baseMarketingCsCartAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "The time the last item was added to the cart.",
    },
    isCartAbandoned: {
      dataType: "Boolean",
      documentation:
        "If the customer has not begun the checkout process or checked out since updating their cart.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned the cart.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer who abandoned the cart.",
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the store that the customer abandoned.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
  },
});

export const baseMarketingCsBrowseAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the browsing last occurred.",
    },
    isBrowseAbandoned: {
      dataType: "Boolean",
      documentation:
        "If the customer has not added anything to the cart or placed an order since browsing.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
  },
});

export const baseMarketingCsCheckoutAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the last customer action on checkout occurred.",
    },
    isCheckoutAbandoned: {
      dataType: "Boolean",
      documentation:
        "If the customer has not placed an order since starting checkout.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
    checkout: {
      dataType: "Maybe Shopify Checkout",
      documentation: "Data about the customer's checkout session",
    },
    // TODO: booka66 - Remove this legacy cartSubtotal field once all legacy cart subtotal automations are migrated to use cartCondition
    cartSubtotal: {
      dataType: "Maybe Number",
      documentation:
        "The subtotal amount of the abandoned cart (in the store's currency).",
    },
    cartProductCount: {
      dataType: "Maybe Number",
      documentation: "The number of unique products in the abandoned cart.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    cartCondition: {
      dataType: "Checkout Context Predicate",
      documentation:
        "Branch based on products in the abandoned cart (collections, specific products, tags, product count, or subtotal).",
      hideFromInsertionModal: true,
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the store that the customer abandoned.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
    productInCartName: {
      dataType: "Maybe Text",
      documentation: "The name of the first product in the abandoned checkout.",
    },
  },
});

export const exampleBaseMarketingCsCartAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCsCartAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isCartAbandoned: true,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
};

export const exampleBaseMarketingCsBrowseAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCsBrowseAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isBrowseAbandoned: true,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
};

export const exampleBaseMarketingCsCheckoutAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCsCheckoutAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  isCheckoutAbandoned: true,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  checkout: {
    attributes: [],
    billingAddress: null,
    currencyCode: CurrencyCode.Usd,
    discountApplications: [],
    email: "john.doe@example.com",
    lineItems: [],
    order: null,
    shippingAddress: null,
    shippingLine: null,
    subtotalPrice: { amount: 100, currencyCode: CurrencyCode.Usd },
    phone: "+1234567890",
    token: "",
    totalPrice: { amount: 105, currencyCode: CurrencyCode.Usd },
    totalTax: { amount: 5, currencyCode: CurrencyCode.Usd },
    transactions: [],
  },
  cartSubtotal: 100,
  cartProductCount: 3,
  cartCondition: false,
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
  productInCartName: "Example Product",
};

export const exampleBaseMarketingEmailSchemaInstance: SchemaInstance<
  typeof baseMarketingEmailSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerEmail: "john@fake.com",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  viewInBrowserLink: undefined, // gets populated later
  cartContext: dummyCartContext(true),
  customerFirstName: undefined,
  customerLastName: undefined,
  customerFullName: undefined,
};

export const exampleSegmentMembershipChangeSchemaInstance: SchemaInstance<
  typeof customerGroupMembershipChangeSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  cartContext: dummyCartContext(true),
  segment: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: undefined,
  customerLastName: undefined,
  customerFullName: undefined,
};

export const baseMarketingSmsSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerPhoneNumber: {
      dataType: "Phone",
      documentation: "The phone number of the customer.",
    },
    customer: { dataType: "Customer", documentation: "The customer object." },
  },
});

const exampleBaseMarketingSignupSchema: SchemaInstance<
  typeof baseMarketingSignupSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  alreadySubscribed: false,
  entryPoint: MarketingEntryPoint.SIGNUP_FORM,
  cartContext: dummyCartContext(true),
  customerFirstName: undefined,
  customerLastName: undefined,
  customerFullName: undefined,
  formId: "66f8d6d38fd4c4b4d1c19876",
  customerEventId: "67f8d6d38fd4c4b4d1c19876",
};

export const exampleEmailMarketingSignupSchema: SchemaInstance<
  typeof emailMarketingSignupSchema
> = {
  ...exampleBaseMarketingSignupSchema,
  discountCode: "EMAILABCD",
  customerEmail: "john@fake.com",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
};

export const smsMarketingSignupSchema = defineSchema({
  fields: {
    ...baseMarketingSignupSchema.fields,
    discountCode: {
      dataType: "Maybe Text",
      documentation: "The discount code to share with the customer.",
    },
    customerPhoneNumber: {
      dataType: "Phone",
      documentation:
        "The phone number of the customer who signed up for marketing SMS.",
    },
  },
});

export const exampleSmsMarketingSignupSchema: SchemaInstance<
  typeof smsMarketingSignupSchema
> = {
  ...exampleBaseMarketingSignupSchema,
  customerPhoneNumber: "+1234567890",
  discountCode: "SMSABCD",
};

export const exampleBaseMarketingBackInStockSchemaInstance: SchemaInstance<
  typeof baseMarketingBackInStockSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  productVariantId: "123",
  backInStockProductUrl: "https://example.com/products/example-product",
  backInStockProductTitle: "Example Product",
  unsubscribeLink: "https://example.com/unsubscribe",
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  oldInventoryCount: 0,
  newInventoryCount: 10,
};

export const exampleBaseMarketingLowInventorySchemaInstance: SchemaInstance<
  typeof baseMarketingLowInventorySchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  productVariantId: "123",
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
};

export const marketingDateSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    date: { dataType: "Date", documentation: "The date of the event." },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
  },
});

export const exampleMarketingDateSchemaInstance: SchemaInstance<
  typeof marketingDateSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  date: getPlainDateFromDateUtc(new Date()),
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
};

export const baseMarketingPriceDropSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    numericVariantId: {
      dataType: "Text",
      documentation: "The product variant ID that had a price drop.",
    },
    productTitle: {
      dataType: "Text",
      documentation: "The title of the product that had a price drop.",
    },
    variantTitle: {
      dataType: "Text",
      documentation: "The title of the variant that had a price drop.",
    },
    formattedPreviousPrice: {
      dataType: "Text",
      documentation: "Previous price formatted with currency symbol.",
    },
    formattedCurrentPrice: {
      dataType: "Text",
      documentation: "Current price formatted with currency symbol.",
    },
    formattedSavings: {
      dataType: "Text",
      documentation: "Savings amount formatted with currency symbol.",
    },
    formattedPriceDropPercentage: {
      dataType: "Text",
      documentation: "Formatted price drop percentage (e.g. '20%').",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation: "Link to unsubscribe from price drop notifications.",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation: "One-click unsubscribe link for compliance.",
    },
  },
});

export const marketingWarrantyRegistrationSchema = defineSchema({
  fields: {
    ...baseMarketingEmailSchema.fields,
    ...discountCodeSchema.fields,
    shopNowLink: {
      dataType: "Maybe Url",
      documentation: "The link to the product on the store.",
    },
    product: {
      dataType: "Product",
      documentation:
        "The product that the customer is registering a warranty for.",
    },
    teamName: {
      dataType: "Text",
      documentation:
        "The name of the team that the customer is registering a warranty for.",
    },
    teamEmail: {
      dataType: "Email",
      documentation:
        "The email of the team that the customer is registering a warranty for.",
    },
  },
});

export const exampleMarketingWarrantyRegistrationSchemaInstance: SchemaInstance<
  typeof marketingWarrantyRegistrationSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  // cspell:disable-next-line
  discountCode: "WARRANTYABCD",
  customerEmail: "john@fake.com",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  viewInBrowserLink: undefined, // gets populated later
  shopNowLink: "https://example.com/shop-now",
  teamName: "Team Name",
  teamEmail: "team@fake.com",
  product: {
    id: 123,
    title: "T-Shirt",
    variantTitle: "Red T-Shirt",
    image: { src: "https://example.com/image.jpg" },
    price: 100,
    priceSet: { presentment_money: { amount: 100, currency_code: "USD" } },
    isRedo: false,
    requiresShipping: true,
    tags: ["tag1", "tag2"],
    properties: { prop1: "value1", prop2: "value2" },
    sku: "123",
    green_return: false,
    vendor: "vendor",
    handle: "handle",
    compareAtPrice: "100",
  },
};

export const exampleBaseMarketingPriceDropSchemaInstance: SchemaInstance<
  typeof baseMarketingPriceDropSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  numericVariantId: "123456",
  productTitle: "Wireless Headphones",
  variantTitle: "Black",
  formattedPreviousPrice: "$99.99",
  formattedCurrentPrice: "$79.99",
  formattedSavings: "$20.00",
  formattedPriceDropPercentage: "20%",
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
};

export const baseOosExchangeRestockedSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    ...discountCodeSchema.fields,
    // Customer contact
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    // Product info
    productTitle: {
      dataType: "Text",
      documentation: "The title of the product that is back in stock.",
    },
    variantTitle: {
      dataType: "Maybe Text",
      documentation: "The variant title (e.g., size/color) of the product.",
    },
    productImageUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the product image.",
    },
    variantId: {
      dataType: "Text",
      documentation: "The Shopify variant ID of the product.",
    },
    productId: { dataType: "Text", documentation: "The Shopify product ID." },
    // Return context
    returnId: {
      dataType: "Text",
      documentation: "The ID of the original return.",
      hideFromInsertionModal: true,
    },
    fireCount: {
      dataType: "Number",
      documentation:
        "The number of times this trigger has fired for this return.",
      hideFromInsertionModal: true,
    },
    // URLs
    storeUrl: { dataType: "Maybe Url", documentation: "The URL of the store." },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL to add the product to cart and checkout.",
    },
    // Unsubscribe
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from notifications. Only displays when subscribed.",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation: "One-click unsubscribe link for email compliance.",
    },
    // Timestamp
    time: {
      dataType: "DateTime",
      documentation: "When the product came back in stock.",
    },
  },
});

export const exampleBaseOosExchangeRestockedSchemaInstance: SchemaInstance<
  typeof baseOosExchangeRestockedSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  discountCode: undefined,
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  productTitle: "Classic T-Shirt",
  variantTitle: "Medium / Navy Blue",
  productImageUrl: "https://example.com/product-image.jpg",
  variantId: "12345678",
  productId: "87654321",
  returnId: "ret_abc123",
  fireCount: 1,
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/cart/12345678:1",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  time: Temporal.Now.instant(),
  cartContext: {
    existingCartId: null,
    productIdsForRecommendations: ["87654321"],
    alreadyInCart: [],
  },
};

export const baseRefundReturnSubmittedSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    ...discountCodeSchema.fields,
    // Customer contact
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    // Return context
    returnId: {
      dataType: "Text",
      documentation: "The ID of the submitted return.",
      hideFromInsertionModal: true,
    },
    returnNumber: {
      dataType: "Maybe Text",
      documentation: "The return number (external ID).",
    },
    refundAmount: {
      dataType: "Maybe Text",
      documentation: "The formatted refund amount (e.g., '$49.99').",
    },
    // Return status (refreshed after wait steps)
    returnStatus: {
      dataType: "Text",
      documentation:
        "Current status of the return (e.g., OPEN, COMPLETE, REJECTED). Refreshed after wait steps.",
    },
    returnIsProcessed: {
      dataType: "Boolean",
      documentation:
        "Whether the return has been processed (status is COMPLETE or REJECTED). Refreshed after wait steps.",
    },
    // Marker field for schema refresh mechanism
    isRefundReturnSubmitted: {
      dataType: "Boolean",
      documentation: "Internal marker for schema refresh mechanism.",
      hideFromInsertionModal: true,
    },
    // Returned products
    returnedProductTitles: {
      dataType: "Multiple Text",
      documentation: "The titles of the products being returned.",
    },
    returnedProductCount: {
      dataType: "Number",
      documentation: "The number of products being returned.",
    },
    // Primary recommendation (for SMS/email preview)
    primaryRecommendationTitle: {
      dataType: "Maybe Text",
      documentation: "The title of the top recommended product.",
    },
    primaryRecommendationPrice: {
      dataType: "Maybe Text",
      documentation: "The formatted price of the top recommended product.",
    },
    primaryRecommendationReason: {
      dataType: "Maybe Text",
      documentation: "AI-generated reason for the primary recommendation.",
    },
    primaryRecommendationIsSameProduct: {
      dataType: "Boolean",
      documentation:
        "Whether the primary recommendation is a variant of a product the customer returned (true) vs. a completely different product (false).",
    },
    // URLs
    storeUrl: { dataType: "Maybe Url", documentation: "The URL of the store." },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "URL to add recommended products to cart.",
    },
    returnPortalUrl: {
      dataType: "Maybe Url",
      documentation: "URL to the customer's return status page.",
    },
    // Unsubscribe
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from notifications. Only displays when subscribed.",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation: "One-click unsubscribe link for email compliance.",
    },
    // Timestamp
    time: {
      dataType: "DateTime",
      documentation: "When the return was submitted.",
    },
  },
});

export const exampleBaseRefundReturnSubmittedSchemaInstance: SchemaInstance<
  typeof baseRefundReturnSubmittedSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  discountCode: undefined,
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  returnId: "ret_abc123",
  returnNumber: "RET-12345",
  refundAmount: "$49.99",
  returnStatus: "OPEN",
  returnIsProcessed: false,
  isRefundReturnSubmitted: true,
  returnedProductTitles: ["Classic T-Shirt", "Blue Jeans"],
  returnedProductCount: 2,
  primaryRecommendationTitle: "Premium Polo Shirt",
  primaryRecommendationPrice: "$59.99",
  primaryRecommendationReason:
    "Based on your style preferences, you might love this polo.",

  primaryRecommendationIsSameProduct: false,
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/cart/123:1",
  returnPortalUrl: "https://example.com/returns/RET-12345",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  time: Temporal.Now.instant(),
  cartContext: {
    existingCartId: null,
    productIdsForRecommendations: [],
    alreadyInCart: [],
  },
};

export const baseExchangeProcessedWithCreditSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    ...discountCodeSchema.fields,
    // Customer contact
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    // Return context
    returnId: {
      dataType: "Text",
      documentation: "The ID of the processed return.",
      hideFromInsertionModal: true,
    },
    returnNumber: {
      dataType: "Maybe Text",
      documentation: "The return number (external ID).",
    },
    storeCreditAmount: {
      dataType: "Text",
      documentation:
        "The formatted leftover store credit amount (e.g., '$25.00').",
    },
    // Exchange product info
    exchangeProductTitles: {
      dataType: "Multiple Text",
      documentation: "The titles of the products received in the exchange.",
    },
    exchangeProductCount: {
      dataType: "Number",
      documentation: "The number of products received in the exchange.",
    },
    // Abandoned cart / recommendation context
    hasAbandonedCart: {
      dataType: "Boolean",
      documentation:
        "Whether the customer has items in their abandoned cart. Use this to branch automation logic.",
    },
    // Primary recommendation (when no abandoned cart)
    primaryRecommendationTitle: {
      dataType: "Maybe Text",
      documentation:
        "The title of the top recommended product (used when no abandoned cart).",
    },
    primaryRecommendationPrice: {
      dataType: "Maybe Text",
      documentation:
        "The formatted price of the top recommended product (used when no abandoned cart).",
    },
    primaryRecommendationReason: {
      dataType: "Maybe Text",
      documentation:
        "AI-generated reason for the primary recommendation (used when no abandoned cart).",
    },
    // URLs
    storeUrl: { dataType: "Maybe Url", documentation: "The URL of the store." },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation:
        "URL to checkout - contains either abandoned cart items or recommended products.",
    },
    // Unsubscribe
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "The link to unsubscribe from notifications. Only displays when subscribed.",
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      hideFromInsertionModal: true,
      documentation: "One-click unsubscribe link for email compliance.",
    },
    // Timestamp
    time: {
      dataType: "DateTime",
      documentation: "When the return was processed.",
    },
  },
});

export const exampleBaseExchangeProcessedWithCreditSchemaInstance: SchemaInstance<
  typeof baseExchangeProcessedWithCreditSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  discountCode: undefined,
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  returnId: "ret_abc123",
  returnNumber: "RET-12345",
  storeCreditAmount: "$25.00",
  exchangeProductTitles: ["Classic T-Shirt (Medium, Navy)"],
  exchangeProductCount: 1,
  hasAbandonedCart: false,
  primaryRecommendationTitle: "Premium Polo Shirt",
  primaryRecommendationPrice: "$59.99",
  primaryRecommendationReason:
    "Based on your recent exchange, you might like this polo shirt.",

  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/cart/123:1",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  time: Temporal.Now.instant(),
  cartContext: {
    existingCartId: null,
    productIdsForRecommendations: [],
    alreadyInCart: [],
  },
};

export const loyaltyLionEnrolledSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email of the customer who enrolled in LoyaltyLion.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who enrolled in LoyaltyLion.",
      hideFromConditionStep: true,
    },
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "Link to unsubscribe from marketing emails. Only displays when the customer is subscribed.",
      hideFromConditionStep: true,
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "One-click unsubscribe link for SMTP compliance (RFC 8058). Only displays when the customer is subscribed.",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
  },
});

export const exampleLoyaltyLionEnrolledSchemaInstance: SchemaInstance<
  typeof loyaltyLionEnrolledSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
};

export const loyaltyLionRewardAvailableSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who has a reward available.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who has a reward available.",
      hideFromConditionStep: true,
    },
    unsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "Link to unsubscribe from marketing emails. Only displays when the customer is subscribed.",
      hideFromConditionStep: true,
    },
    oneClickUnsubscribeLink: {
      dataType: "Maybe Url",
      documentation:
        "One-click unsubscribe link for SMTP compliance (RFC 8058). Only displays when the customer is subscribed.",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
    rewardId: {
      dataType: "Text",
      documentation:
        "The unique identifier of the available reward in LoyaltyLion.",
      hideFromConditionStep: true,
    },
    rewardTitle: {
      dataType: "Text",
      documentation:
        "The title/name of the available reward (e.g., '$10 off your next order').",
    },
    claimUrl: {
      dataType: "Maybe Url",
      documentation: "URL where the customer can view and claim their reward.",
    },
    pointCost: {
      dataType: "Number",
      documentation:
        "The number of loyalty points required to claim this reward.",
    },
  },
});

export const exampleLoyaltyLionRewardAvailableSchemaInstance: SchemaInstance<
  typeof loyaltyLionRewardAvailableSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "Sarah",
  customerLastName: "Johnson",
  customerFullName: "Sarah Johnson",
  customerEmail: "sarah.johnson@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  rewardId: "ll_reward_12345",
  rewardTitle: "$10 off your next order",
  claimUrl: "https://example.com/loyalty/rewards/12345",
  pointCost: 500,
};
